import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Home as StationIcon,
  DirectionsCar as UnitIcon,
  Devices as AgentIcon,
  AltRoute as RoutingIcon,
  NotificationsActive as AlertIcon,
  AddAlert as ManualAlertIcon,
  MusicNote as ToneIcon,
  RecordVoiceOver as TTSIcon,
  Email as NotifyIcon,
  List as LogIcon,
  Abc as PronIcon,
  People as PeopleIcon,
  Palette as BrandingIcon,
  Business as AgencyIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import LiveClock from './LiveClock';

const TZ_LABELS: Record<string, string> = {
  'America/New_York': 'Eastern',
  'America/Chicago': 'Central',
  'America/Denver': 'Mountain',
  'America/Los_Angeles': 'Pacific',
  'America/Anchorage': 'Alaska',
  'Pacific/Honolulu': 'Hawaii',
  'America/Phoenix': 'Arizona',
  'America/Indiana/Indianapolis': 'Eastern (IN)',
  'America/Indiana/Knox': 'Central (IN)',
  'America/Indiana/Marengo': 'Eastern (IN)',
  'America/Indiana/Petersburg': 'Eastern (IN)',
  'America/Indiana/Tell_City': 'Central (IN)',
  'America/Indiana/Vevay': 'Eastern (IN)',
  'America/Indiana/Vincennes': 'Eastern (IN)',
  'America/Indiana/Winamac': 'Eastern (IN)',
  'America/Kentucky/Louisville': 'Eastern (KY)',
  'America/Kentucky/Monticello': 'Eastern (KY)',
  'America/North_Dakota/Beulah': 'Central (ND)',
  'America/North_Dakota/Center': 'Central (ND)',
  'America/North_Dakota/New_Salem': 'Central (ND)',
  'America/Detroit': 'Eastern',
  'America/Boise': 'Mountain',
  'America/Juneau': 'Alaska',
  'America/Adak': 'Hawaii-Aleutian',
  'America/Menominee': 'Central',
  'America/Nome': 'Alaska',
  'America/Sitka': 'Alaska',
  'America/Yakutat': 'Alaska',
  'America/Metlakatla': 'Alaska',
  'US/Eastern': 'Eastern',
  'US/Central': 'Central',
  'US/Mountain': 'Mountain',
  'US/Pacific': 'Pacific',
  'US/Alaska': 'Alaska',
  'US/Hawaii': 'Hawaii',
  'US/Arizona': 'Arizona',
  'US/East-Indiana': 'Eastern (IN)',
  'US/Indiana-Starke': 'Central (IN)',
};

function timezoneLabel(tz: string): string {
  return TZ_LABELS[tz] || tz.split('/').pop()?.replace(/_/g, ' ') || 'Local';
}

const DRAWER_WIDTH = 240;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  dividerBefore?: boolean;
  roles?: string[];
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: <DashboardIcon /> },
  { label: 'Manual Alert', path: '/manual-alert', icon: <ManualAlertIcon />, roles: ['dispatcher', 'admin'] },
  { label: 'Agencies', path: '/agencies', icon: <AgencyIcon />, roles: ['admin'] },
  { label: 'Stations', path: '/stations', icon: <StationIcon />, roles: ['admin'] },
  { label: 'Units', path: '/units', icon: <UnitIcon />, roles: ['admin'] },
  { label: 'Agents', path: '/agents', icon: <AgentIcon />, roles: ['admin'] },
  { label: 'Routing Rules', path: '/routing', icon: <RoutingIcon />, roles: ['admin'] },
  { label: 'Alert History', path: '/alerts', icon: <AlertIcon /> },
  { label: 'Alert Tones', path: '/tones', icon: <ToneIcon />, dividerBefore: true, roles: ['admin'] },
  { label: 'Pronunciation', path: '/pronunciation', icon: <PronIcon /> },
  { label: 'TTS Settings', path: '/settings/tts', icon: <TTSIcon /> },
  { label: 'Branding', path: '/branding', icon: <BrandingIcon />, roles: ['admin'] },
  { label: 'Users', path: '/users', icon: <PeopleIcon />, dividerBefore: true, roles: ['admin'] },
  { label: 'Notifications', path: '/settings/notifications', icon: <NotifyIcon />, roles: ['admin'] },
  { label: 'System Log', path: '/settings/system-log', icon: <LogIcon /> },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { username, role, isSuperadmin, tenantTimezone, logout } = useAuth();

  const displayTimezone = tenantTimezone || 'America/Chicago';
  const userRole = isSuperadmin ? 'admin' : (role || 'monitor');

  const visibleNavItems = NAV_ITEMS.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(userRole);
  });

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            WatchCenter Station Alert
          </Typography>
          <LiveClock timezone={displayTimezone} label={timezoneLabel(displayTimezone)} />
          <Divider orientation="vertical" flexItem sx={{ mx: 1.5, borderColor: 'rgba(255,255,255,0.3)' }} />
          <Typography variant="body2" sx={{ mr: 2 }}>
            {username}
          </Typography>
          <Button color="inherit" onClick={logout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="persistent"
        open={drawerOpen}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        <List>
          {visibleNavItems.map((item) => (
            <React.Fragment key={item.path}>
              {item.dividerBefore && <Divider sx={{ my: 1 }} />}
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </React.Fragment>
          ))}
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          ml: drawerOpen ? `${DRAWER_WIDTH}px` : 0,
          transition: 'margin 225ms',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
